import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * ConnectingDotsExhaustion — Contrarian Scalp Strategy
 *
 * How it works:
 * - Scans historical `trendAnalysisData` to find momentum peaks.
 * - If the Dots score hits +5 (max bullish) and then drops to +3 or less, momentum is exhausted (Buy PE).
 * - If the Dots score hits -5 (max bearish) and then bounces to -3 or higher, momentum is exhausted (Buy CE).
 */
export const evaluateConnectingDotsExhaustion: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, gap } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    // Detect Exhaustion via Trend Analysis Data historical rows
    const trendAnalysisResult = context.liveIndicators?.trendAnalysisData;
    const fullTrendData = Array.isArray(trendAnalysisResult) ? trendAnalysisResult : (trendAnalysisResult?.data || []);
    
    // We need at least 5-10 rows to detect a peak and a drop
    if (fullTrendData.length < 5) return null;

    const latestRow = fullTrendData[0];
    const latestScore = latestRow?.dotsScore ?? 0;

    let hitBullishPeak = false;
    let hitBearishBottom = false;

    // Check the last 15 periods (approx 15-30 mins depending on fetch frequency) for a peak
    for (let i = 1; i < Math.min(15, fullTrendData.length); i++) {
        const score = fullTrendData[i]?.dotsScore ?? 0;
        if (score >= 5) hitBullishPeak = true;
        if (score <= -5) hitBearishBottom = true;
    }

    let status: 'BULL' | 'BEAR' | 'Sideways' = 'Sideways';
    let remarks = '';

    // If it hit a +5 bullish peak recently, but the LATEST score dropped to +3 or lower, momentum died
    if (hitBullishPeak && latestScore <= 3 && latestScore >= 0) {
        status = 'BEAR'; // Contrarian: Buy PE
        remarks = `Bullish Exhaustion (Peak: +5 → Now: ${latestScore})`;
    }
    // If it hit a -5 bearish bottom recently, but LATEST bounced to -3 or higher
    else if (hitBearishBottom && latestScore >= -3 && latestScore <= 0) {
        status = 'BULL'; // Contrarian: Buy CE
        remarks = `Bearish Exhaustion (Bottom: -5 → Now: ${latestScore})`;
    }

    if (status === 'Sideways') return null;

    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;
    const optionType = status === 'BULL' ? 'CE' : 'PE';

    let entryPrice = 0;
    const atmRow = liveFlatOpts.find((r: any) => r.strike === atmStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: status,
        strike: atmStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: remarks
    };
};
